import React, { useState, useEffect } from 'react';
import { Button } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { deleteDoc, getDocs, collection, doc, query, updateDoc } from 'firebase/firestore';
import { useNavigate, useParams } from 'react-router-dom';

import Header from '../../Header';
import AlertDialog from '../../components/AlertDialog';
import ProjectCard from '../ProjectCard';
import { db } from '../../../firebase';
import { deleteProjectStorage } from '../../../utils/storageHelpers';

import './styles.css';

function Projects() {
	const [nameFilter, setNameFilter] = useState('');
	const [loading, setLoading] = useState(true);
	const [openAlert, setOpenAlert] = useState(false);
	const [projects, setProjects] = useState([]);
	const [itemToDelete, setItemToDelete] = useState('');
	const [disableButtons, setDisableButtons] = useState(false);

	const { organizationId, directoryId } = useParams();
	const navigate = useNavigate();

	useEffect(() => {
		fetchProjects();
	}, []);

	const fetchProjects = async () => {
		setLoading(true);
		try {
			const projectsRef = collection(db, 'projects');
			const q = query(projectsRef);
			const querySnapshot = await getDocs(q);
			const allProjects = querySnapshot.docs.map((doc) => ({
				id: doc.id,
				name: doc.data().name,
				creationDate: doc.data().creationDate,
				images: doc.data().images,
				csvURL: doc.data().csvURL,
				directoryId: doc.data().directoryId,
				organizationId: doc.data().organizationId,
			}));
			// Filter projects for this directory
			const directoryProjects = allProjects.filter(p => p.directoryId === directoryId);
			setProjects(directoryProjects);
		} finally {
			setLoading(false);
		}
	};

	const getFilteredProjects = (projects) => {
		const filteredItemsOnNameFilter = projects.filter((item) => {
			let filterName = nameFilter.toLowerCase();
			const names = item.name.split(' ');
			for (let i = 0; i < names.length; i += 1) {
				const name = names[i];
				if (name.toLowerCase().startsWith(filterName)) {
					return true;
				}
			}
			return false;
		});
		return filteredItemsOnNameFilter;
	};

	const closeAlert = () => {
		if (!disableButtons) {
			setOpenAlert(false);
		}
	};

	const onDeleteProjectClick = (project) => {
		setItemToDelete(project);
		setOpenAlert(true);
	};

	const deleteProject = async (project) => {
		setDisableButtons(true);
		const projectRef = doc(db, 'projects', project.id);
		try {
			const folderPath = project.id; // New path structure
			await deleteProjectStorage(folderPath);
			await deleteDoc(projectRef);
			fetchProjects();
			setOpenAlert(false);
		} catch (error) {
			console.error('Error deleting project: ', error);
		} finally {
			setDisableButtons(false);
		}
	};

	const onBackClick = () => {
		navigate(`/admin/organizations/${organizationId}/directories`);
	};

	const onAlertBoxYesClick = () => {
		deleteProject(itemToDelete);
	};

	const onAddProjectClick = () => {
		navigate(`/admin/organizations/${organizationId}/directories/${directoryId}/new-project`);
	};

	const renameProject = async (project, newName) => {
		const projectRef = doc(db, 'projects', project.id);

		try {
			await updateDoc(projectRef, { name: newName });
			fetchProjects(); 
		} catch (error) {
			console.error('Error renaming project:', error);
		}
	};

	const filteredProjects = getFilteredProjects(projects);

	return (
		<div className="projs__root">
			<Header />
			<div className="projs__content">

				{/* Back navigation */}
				<button className="projs__back-btn" onClick={onBackClick}>
					<ArrowBackIcon fontSize="small" />
					Back to projects
				</button>

				{/* Page header */}
				<div className="projs__header">
					<div>
						<h1 className="projs__title">Sections</h1>
						<p className="projs__subtitle">Manage and access inspection sections.</p>
					</div>
				</div>

				{/* Toolbar */}
				<div className="projs__toolbar">
					<div className="projs__search-wrap">
						<SearchIcon className="projs__search-icon" fontSize="small" />
						<input
							className="projs__search-input"
						placeholder="Search sections…"
							value={nameFilter}
							onChange={(e) => setNameFilter(e.target.value)}
						/>
					</div>
					<Button
						variant="contained"
						startIcon={<AddIcon />}
						onClick={onAddProjectClick}
						sx={{
							background: '#0f2d4e',
							fontFamily: '"Open Sans", sans-serif',
							fontWeight: 600,
							textTransform: 'none',
							borderRadius: '8px',
							padding: '8px 20px',
							'&:hover': { background: '#1a4a7a' },
						}}
					>
						Add section
					</Button>
				</div>

				{/* Results count */}
				{projects.length > 0 && (
					<p className="projs__results-label">
						{filteredProjects.length}{' '}
						{filteredProjects.length === 1 ? 'section' : 'sections'}
						{nameFilter ? ` matching “${nameFilter}”` : ''}
					</p>
				)}

				{/* Grid */}
				<div className="projs__grid">
					{loading ? (
						Array.from({ length: 4 }).map((_, i) => (
							<div key={i} className="projs__skeleton-card">
								<div className="projs__skeleton-thumb admin-skeleton" />
								<div className="projs__skeleton-body">
									<div className="projs__skeleton-name admin-skeleton" />
									<div className="projs__skeleton-meta admin-skeleton" />
								</div>
							</div>
						))
					) : filteredProjects.length === 0 ? (
						<div className="projs__empty">
							<AssignmentOutlinedIcon sx={{ fontSize: 52, color: 'rgba(15,45,78,0.18)', mb: 1 }} />
							<p className="projs__empty-title">
								{nameFilter ? 'No sections match your search' : 'No sections yet'}
							</p>
							{!nameFilter && (
								<>
									<p className="projs__empty-hint">
									Create your first section to start organizing inspection imagery.
									</p>
									<Button
										variant="contained"
										startIcon={<AddIcon />}
										onClick={onAddProjectClick}
										sx={{
											mt: 2,
											background: '#0f2d4e',
											fontFamily: '"Open Sans", sans-serif',
											fontWeight: 600,
											textTransform: 'none',
											borderRadius: '8px',
											'&:hover': { background: '#1a4a7a' },
										}}
									>
										Add section
									</Button>
								</>
							)}
						</div>
					) : (
						filteredProjects.map((project) => (
							<ProjectCard
								key={project.id}
								project={project}
								organizationId={organizationId}
								onEdit={() => {}}
								onDelete={onDeleteProjectClick}
								onRename={renameProject}
							/>
						))
					)}
				</div>

				<AlertDialog
					open={openAlert}
					onYesClick={onAlertBoxYesClick}
					handleClose={closeAlert}
					disableButtons={disableButtons}
					itemToDelete={itemToDelete}
				/>
			</div>
		</div>
	);
}

export default Projects;
